import { anyOf, arrayOf, maybe, objectOf } from "@altostra/type-validations";
import { boolean, nullValidation, number, string } from "@altostra/type-validations/lib/primitives";

import type { AgentPreviewAsyncResult, CampaignQaRuleBasis, CampaignQaRuleStatus } from "./types";

/**
 * Every member of {@link CampaignQaRuleStatus}. Keyed by the union rather than typed as an
 * array of it so that adding a status without listing it here is a compile error — an array
 * of the union constrains its members but never requires all of them, which would let a new
 * status slip past the guard below and be silently rewritten to "unable_to_verify".
 * Mirrors `Braze::Agentic::QaAgentDefinition::QaResultStatuses`.
 */
const campaignQaRuleStatuses: Record<CampaignQaRuleStatus, true> = {
  pass: true,
  fail: true,
  unable_to_verify: true,
};

/**
 * Narrows an unvalidated value — a status read back out of a stored agent invocation
 * log, say — to a known status. Callers substitute "unable_to_verify" for anything else,
 * matching what the backend does when normalizing a completion it can't read.
 */
export const isCampaignQaRuleStatus = (value: unknown): value is CampaignQaRuleStatus =>
  typeof value === "string" && Object.hasOwn(campaignQaRuleStatuses, value);

/**
 * Every member of {@link CampaignQaRuleBasis}, keyed by the union for the same reason as
 * the statuses above. Mirrors `Braze::Agentic::QaAgentDefinition::QaResultBases`.
 */
const campaignQaRuleBases: Record<CampaignQaRuleBasis, true> = {
  evidence: true,
  judgment: true,
};

/**
 * Narrows an unvalidated value to a known basis. Callers drop anything else, matching
 * what the backend does when normalizing a completion.
 */
export const isCampaignQaRuleBasis = (value: unknown): value is CampaignQaRuleBasis =>
  typeof value === "string" && Object.hasOwn(campaignQaRuleBases, value);

const nullableString = anyOf(string, nullValidation);
const nullableArrayOfStrings = anyOf(arrayOf(string), nullValidation);

export const isAgentPreviewAsyncResult = objectOf<AgentPreviewAsyncResult>({
  status: number,
  result: maybe(nullableString),
  execution_time_ms: maybe(number),
  errors: maybe(nullableArrayOfStrings),
  fallback_result: maybe(nullableString),
  error_code: maybe(string),
  tool_execution_logs: maybe(
    arrayOf(
      objectOf({
        name: string,
        arguments: maybe(nullableString),
        duration: maybe(number),
      })
    )
  ),
  execution_usage: maybe(
    objectOf({
      limits: objectOf({
        daily: maybe(number),
        max_daily: maybe(number),
        total: maybe(number),
      }),
      current_usage: objectOf({
        today: maybe(number),
        total: maybe(number),
      }),
      remaining: objectOf({
        today: maybe(number),
        total: maybe(number),
      }),
      exceeded_limit: boolean,
    })
  ),
  qa_results: maybe(
    anyOf(
      arrayOf(
        objectOf({
          rule: string,
          status: string,
          reason: string,
          ruleset_name: maybe(string),
          basis: maybe(string),
        })
      ),
      nullValidation
    )
  ),
  chunked_result_complete: maybe(boolean),
});
